export const BEHAVIOR_MODEL_VERSION = 1 as const;
export const BID_LEVEL_BIN_COUNT = 13;
export const TRUMP_CATEGORY_COUNT = 10;
export const CONFIDENCE_PER_HAND = 0.02;
export const MAX_PERSONALIZATION_WEIGHT = 0.35;
export const HUMAN_PLAYER_INDEX = 0;

export interface BehaviorModel {
  version: 1;
  bidPass: { pass: number; bid: number };
  bidLevelBins: number[];
  trumpCounts: number[];
  earlyTrumpLead: { yes: number; no: number };
  countSlough: { yes: number; no: number };
  partnerWinOvertake: { overtakes: number; supports: number };
  riskByMarks: {
    ahead: { conservative: number; aggressive: number };
    behind: { conservative: number; aggressive: number };
    tied: { conservative: number; aggressive: number };
  };
}

export function createEmptyBehaviorModel(): BehaviorModel {
  return {
    version: BEHAVIOR_MODEL_VERSION,
    bidPass: { pass: 1, bid: 1 },
    bidLevelBins: Array.from({ length: BID_LEVEL_BIN_COUNT }, () => 1),
    trumpCounts: Array.from({ length: TRUMP_CATEGORY_COUNT }, () => 1),
    earlyTrumpLead: { yes: 1, no: 1 },
    countSlough: { yes: 1, no: 1 },
    partnerWinOvertake: { overtakes: 1, supports: 1 },
    riskByMarks: {
      ahead: { conservative: 1, aggressive: 1 },
      behind: { conservative: 1, aggressive: 1 },
      tied: { conservative: 1, aggressive: 1 },
    },
  };
}

export function normalizeBidPass(model: BehaviorModel): { pass: number; bid: number } {
  const total = model.bidPass.pass + model.bidPass.bid;
  return {
    pass: model.bidPass.pass / total,
    bid: model.bidPass.bid / total,
  };
}

export function normalizeBidLevel(model: BehaviorModel, bidValue: number): number {
  const index = Math.min(Math.max(bidValue - 30, 0), BID_LEVEL_BIN_COUNT - 1);
  const total = model.bidLevelBins.reduce((sum, count) => sum + count, 0);
  return (model.bidLevelBins[index] ?? 1) / total;
}

export function trumpCategoryIndex(
  declaration: 'suit' | 'doubles' | 'nello' | 'sevens' | 'marks',
  suit: number | null,
): number {
  if (declaration === 'doubles') return 7;
  if (declaration === 'nello') return 8;
  if (declaration === 'sevens') return 9;
  if (declaration === 'marks') return 6;
  return Math.min(Math.max(suit ?? 0, 0), 6);
}

export function normalizeTrump(model: BehaviorModel, categoryIndex: number): number {
  const total = model.trumpCounts.reduce((sum, count) => sum + count, 0);
  return (model.trumpCounts[categoryIndex] ?? 1) / total;
}

export function computeConfidence(completedHands: number): number {
  return Math.min(1, completedHands * CONFIDENCE_PER_HAND);
}

export function personalizationWeight(confidence: number): number {
  return Math.min(MAX_PERSONALIZATION_WEIGHT, confidence * MAX_PERSONALIZATION_WEIGHT);
}

export function blendLikelihood(
  uniformLikelihood: number,
  personalizedLikelihood: number,
  confidence: number,
): number {
  const weight = personalizationWeight(confidence);
  return uniformLikelihood * (1 - weight) + personalizedLikelihood * weight;
}

export function cloneBehaviorModel(model: BehaviorModel): BehaviorModel {
  return JSON.parse(JSON.stringify(model)) as BehaviorModel;
}
